import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { toLocalInput } from '@/lib/datetime';
import { BidForm, type BidFormValues } from '../components/BidForm';
import { bidTitle } from '../format';
import { useBid, useCreateBid, useUpdateBid } from '../queries';
import type { Bid, BidInput } from '../types';

const str = (v: string | number | null | undefined) =>
  v === null || v === undefined ? '' : String(v);

function toFormValues(b: Bid): Partial<BidFormValues> {
  return {
    channel: b.channel,
    job_id: b.job_id,
    proposal_id: b.proposal_id,
    bidder_profile: b.bidder_profile,
    owner: b.owner ? String(b.owner) : '',
    job_title: b.job_title,
    job_url: b.job_url,
    job_type: b.job_type,
    client_budget: b.client_budget,
    experience_level: b.experience_level,
    submitted_at: toLocalInput(b.submitted_at),
    currency: b.currency,
    bid_rate: str(b.bid_rate),
    bid_amount: str(b.bid_amount),
    profile_rate_at_submit: str(b.profile_rate_at_submit),
    boosted: b.boosted,
    connects_cost: str(b.connects_cost),
    connects_spent: str(b.connects_spent),
    connects_refunded: str(b.connects_refunded),
    bid_filter_verdict: b.bid_filter_verdict,
    bid_range_low: str(b.bid_range_low),
    bid_range_median: str(b.bid_range_median),
    bid_range_high: str(b.bid_range_high),
    client_country: b.client_country,
    client_hire_rate_pct: str(b.client_hire_rate_pct),
    client_total_spent: str(b.client_total_spent),
    status: b.status,
    viewed_by_client: b.viewed_by_client,
    replied_at: toLocalInput(b.replied_at),
    room_id: b.room_id,
    contract_id: b.contract_id,
  };
}

export function BidFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const bidQuery = useBid(publicId);
  const create = useCreateBid();
  const update = useUpdateBid(publicId);
  const back = isEdit ? `/crm/bids/${publicId}` : '/crm/bids';

  async function handleSubmit(values: Partial<BidInput>) {
    // In create mode the form sends every field (see BidForm `mode`).
    const saved = isEdit
      ? await update.mutateAsync(values)
      : await create.mutateAsync(values as BidInput);
    navigate(`/crm/bids/${saved.public_id}`);
  }

  if (isEdit && bidQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }
  if (isEdit && bidQuery.isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/bids')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(bidQuery.error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const bid = bidQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(back)} aria-label="Back">
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${bid ? bidTitle(bid) : 'bid'}` : 'New bid'}
        </h1>
      </div>
      {isEdit && bid?.origin === 'sync' ? (
        <p className="text-muted-foreground text-sm">
          This bid is synced. Fields you change here are kept — the sync never overwrites them.
        </p>
      ) : null}
      <BidForm
        mode={isEdit ? 'edit' : 'create'}
        lockKey={bid?.origin === 'sync'}
        submitLabel={isEdit ? 'Save changes' : 'Create bid'}
        defaultValues={bid ? toFormValues(bid) : undefined}
        onSubmit={handleSubmit}
        onCancel={() => navigate(back)}
      />
    </div>
  );
}
